<template>
  <Transition name="fade-slide">
    <div v-if="show" class="max-w-[1600px] mx-auto mt-4 p-4 sm:p-6 bg-white rounded-lg border border-gray-200">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('search.priceRange') }}</label>
          <FilterPrice
            :value="filters.price"
            @update:value="onUpdate('price', $event)"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('search.minSize') }}</label>
          <FilterSize
            :value="filters.size"
            @update:value="onUpdate('size', $event)"
          />
        </div>
      </div>
      <div v-if="loadingTags" class="mt-8 pt-8 border-t border-gray-100">
        <label class="block text-sm font-medium text-gray-700 mb-4">{{ t('property.features_and_amenities') }}</label>
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <div v-for="i in 10" :key="i" class="h-8 bg-gray-100 animate-pulse rounded"></div>
        </div>
      </div>
      <div v-else-if="tagGroups.length && tagGroups.some(([, tags]) => tags.length)" class="mt-8 pt-8 border-t border-gray-100">
        <label class="block text-sm font-medium text-gray-700 mb-4">{{ t('property.features_and_amenities') }}</label>
        <FilterTags
          :model-value="filters.labels"
          @update:model-value="onUpdate('labels', $event)"
          :groups="tagGroups"
        />
      </div>
      <div class="flex justify-end mt-1">
        <button
          v-if="isFiltersActive"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          @click="$emit('clear-filters')"
        >
          {{ t('search.clearFilters') }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPrice from './filters/FilterPrice.vue';
import FilterSize from './filters/FilterSize.vue';
import FilterTags from './filters/FilterTags.vue';
import type { Filters, Facets } from '@repo/types';
import type { TagGroups } from '@repo/ui';

const props = defineProps<{
  show: boolean;
  filters: Filters;
  facets?: Facets;
  tagGroups: TagGroups;
  loadingTags?: boolean;
}>();

const emit = defineEmits(['clear-filters', 'update:filters']);

const { t } = useI18n();

function onUpdate<T extends keyof Filters>(key: T, value: Filters[T]) {
  emit('update:filters', { ...props.filters, [key]: value });
}

const isFiltersActive = computed(() => {
  if (!props.filters) return false;
  return [
    props.filters.labels?.length > 0,
    props.filters.price?.min > 0 || props.filters.price?.max !== undefined,
    props.filters.size?.min > 0 || props.filters.size?.max !== undefined
  ].filter(Boolean).length > 0;
});
</script>
